import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { CalendarPlus, Receipt, Users, BarChart3, ShieldCheck, Building2 } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../styles';
import { resolveCanUseSelfService } from '../resolveDashboardGates';

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 8px;
  border: 1px solid #F0F2F5;
  border-radius: 6px;
  background: #FAFBFC;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  &:hover {
    background: #E8F4FD;
    border-color: ${({ theme }) => theme.colors.primary.main};
    transform: translateY(-1px);
  }

  svg {
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const ActionLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
`;

// Her kısayol, sol menünün AYNI hedefe koyduğu kapıdan geçer (MenuConfig + SidebarMenu.hasAccess):
//  - `module`: kiracı aboneliği + koltuk. /my-* sayfaları kişiseldir; menüde `privileges: []`,
//    rotada ayrıcalık yok. Eskiden canRead soruluyordu ve rolsüz çalışan yalnız "Çalışanlar"ı
//    görüyordu — gerekçe resolveDashboardGates.js'te.
//  - `privilege`: menü öğesinin ve rotanın istediği ayrıcalık. Geçiş Kontrolü'nde ikisi birden:
//    menü grubu aboneliğe bağlı (moduleGated), öğe AC.Write ister — kurumsal adminin joker
//    ayrıcalığı aboneliği karşılamaz.
const ACTIONS = [
  { labelKey: 'dashboard.createLeave', icon: CalendarPlus, path: '/my-leaves', module: 'LM' },
  { labelKey: 'dashboard.createExpense', icon: Receipt, path: '/my-expenses', module: 'EX' },
  { labelKey: 'dashboard.viewEmployees', icon: Users, path: '/employees', privilege: ['EM', 'Read'] },
  { labelKey: 'dashboard.viewSurveys', icon: BarChart3, path: '/my-surveys', module: 'SV' },
  { labelKey: 'dashboard.viewAccessControl', icon: ShieldCheck, path: '/access-permissions', module: 'AC', privilege: ['AC', 'Write'] },
  { labelKey: 'dashboard.viewAssets', icon: Building2, path: '/my-assets', module: 'AM' },
];

export default function QuickActionsCard({ hasModule, hasSeat, hasPermission }) {
  const { t } = useTranslation();
  const history = useHistory();

  const actions = ACTIONS.filter((a) => {
    if (a.module && !resolveCanUseSelfService(a.module, { hasModule, hasSeat })) return false;
    return !a.privilege || hasPermission(...a.privilege);
  }).map((a) => ({ ...a, label: t(a.labelKey) }));

  if (actions.length === 0) return null;

  return (
    <Card $delay="0.2s">
      <CardHeader>
        <CardTitle>{t('dashboard.quickActions')}</CardTitle>
      </CardHeader>
      <ActionsGrid>
        {actions.map(action => {
          const Icon = action.icon;
          return (
            <ActionButton key={action.path} onClick={() => history.push(action.path)}>
              <Icon size={22} />
              <ActionLabel>{action.label}</ActionLabel>
            </ActionButton>
          );
        })}
      </ActionsGrid>
    </Card>
  );
}
